import {
  ArrowRight,
  Cloud,
  Database,
  HardDriveUpload,
  LockKeyhole,
  MessageCircleMore,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

const features = [
  {
    icon: MessageCircleMore,
    title: "가볍게 시작하는 대화",
    body: "복잡한 메뉴보다 대화에 집중합니다. 팀 채팅, 개인 메모, 프로젝트별 공간을 한곳에서 관리합니다.",
  },
  {
    icon: ShieldCheck,
    title: "안전한 권한 경계",
    body: "로그인 세션과 방 멤버십을 서버에서 다시 확인해 참여한 사람만 메시지와 첨부파일에 접근할 수 있습니다.",
  },
  {
    icon: Cloud,
    title: "독립된 Cloudflare 운영",
    body: "기존 서비스와 다른 Account의 Pages, D1, R2를 사용해 트래픽과 저장공간을 분리합니다.",
  },
];

function Home({ backend, onEnter }) {
  const connected = backend.database === "ready" && backend.storage === "ready";

  return (
    <main className="home-page">
      <header className="home-nav">
        <a className="home-brand" href="#top" aria-label="채팅 서비스 홈">
          <span><MessageCircleMore size={20} /></span>
          <strong>채팅 서비스</strong>
        </a>
        <nav aria-label="홈 화면 메뉴">
          <a href="#about">소개</a>
          <a href="#infrastructure">운영 구조</a>
          <button type="button" onClick={onEnter}>채팅 시작</button>
        </nav>
      </header>

      <section className="home-hero" id="top">
        <div className="hero-copy">
          <span className="eyebrow"><Sparkles size={15} /> 서로의 하루가 이어지는 공간</span>
          <h1>대화는 편안하게,<br />운영은 <em>독립적으로.</em></h1>
          <p>작은 팀과 모임이 안심하고 이야기를 나눌 수 있는 채팅 서비스입니다. 기존 운영 서비스에 부담을 주지 않도록 별도의 Cloudflare 환경에서 시작합니다.</p>
          <div className="hero-actions">
            <button className="primary-cta" type="button" onClick={onEnter}>채팅 화면 보기 <ArrowRight size={18} /></button>
            <a href="#about">서비스 알아보기</a>
          </div>
          <div className="home-status" aria-live="polite">
            <i className={connected ? "home-status--ready" : ""} />
            <span>{backend.checking ? "Cloudflare 연결을 확인하고 있어요" : connected ? "D1과 R2가 준비되었습니다" : "안전한 연결 준비 중"}</span>
          </div>
        </div>

        <div className="hero-preview" aria-label="채팅 화면 예시">
          <div className="preview-window-bar"><i /><i /><i /><span>우리 팀</span></div>
          <div className="preview-room">
            <div className="preview-message preview-message--left"><b>민</b><p>오늘 일정은 여기서 같이 정리해요.</p></div>
            <div className="preview-message preview-message--right"><p>좋아요! 필요한 자료도 함께 올릴게요.</p><b>나</b></div>
            <div className="preview-message preview-message--left"><b>도</b><p>대화 기록은 D1에 안전하게 보관해요.</p></div>
          </div>
          <div className="preview-composer"><span>메시지를 입력하세요</span><ArrowRight size={17} /></div>
          <span className="floating-note floating-note--members"><Users size={15} /> 함께하는 멤버 3명</span>
          <span className="floating-note floating-note--secure"><LockKeyhole size={15} /> 서버 권한 확인</span>
        </div>
      </section>

      <section className="home-section" id="about">
        <div className="section-heading">
          <span>왜 이렇게 시작하나요?</span>
          <h2>채팅에 필요한 기본을<br />처음부터 단단하게</h2>
          <p>보이는 화면뿐 아니라 데이터와 파일이 어디에 저장되고, 누가 접근할 수 있는지까지 함께 설계합니다.</p>
        </div>
        <div className="feature-grid">
          {features.map(({ icon: Icon, title, body }, index) => (
            <article className="feature-card" key={title}>
              <span className="feature-number">0{index + 1}</span>
              <span className="feature-icon"><Icon size={22} /></span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="infrastructure-section" id="infrastructure">
        <div className="infrastructure-copy">
          <span>독립 운영 구조</span>
          <h2>대화와 파일을<br />알맞은 곳에 나눠 저장합니다.</h2>
          <p>구조화된 대화 기록은 D1에, 이미지와 첨부파일은 R2에 둡니다. Pages Functions가 인증과 권한을 확인한 뒤 필요한 데이터만 전달합니다.</p>
          <button type="button" onClick={onEnter}>현재 채팅 화면 확인 <ArrowRight size={17} /></button>
        </div>
        <div className="infrastructure-map" aria-label="Cloudflare 서비스 구성">
          <div className="infra-node infra-node--main"><Cloud size={25} /><strong>Pages Functions</strong><span>요청과 권한 확인</span></div>
          <div className="infra-line infra-line--one" />
          <div className="infra-line infra-line--two" />
          <div className="infra-node infra-node--d1"><Database size={23} /><strong>D1</strong><span>사용자·방·메시지</span></div>
          <div className="infra-node infra-node--r2"><HardDriveUpload size={23} /><strong>R2</strong><span>이미지·첨부파일</span></div>
        </div>
      </section>

      <footer className="home-footer">
        <div><MessageCircleMore size={19} /><strong>채팅 서비스</strong></div>
        <p>별도 Cloudflare Account에서 독립적으로 운영됩니다.</p>
      </footer>
    </main>
  );
}

export default Home;
